import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Box,
  Text,
} from "@chakra-ui/react";
import { gql, useMutation } from "@apollo/client";

// Update client mutation
const UPDATE_CLIENT = gql`
  mutation UpdateClient($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      fName
      lName
      email
    }
  }
`;

interface CaptureUserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onUpdateSuccess?: () => void;
  currentUserData?: {
    fName?: string;
    lName?: string;
    email?: string;
  };
}

export const CaptureUserDetailsModal: React.FC<CaptureUserDetailsModalProps> = ({
  isOpen,
  onClose,
  userId,
  onUpdateSuccess,
  currentUserData
}) => {
  const [formData, setFormData] = useState({
    firstName: currentUserData?.fName || "",
    lastName: currentUserData?.lName || "",
    email: currentUserData?.email || "",
  });
  const toast = useToast();

  const [updateClient, { loading }] = useMutation(UPDATE_CLIENT);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      const { data: _data } = await updateClient({
        variables: {
          id: userId,
          input: {
            fName: formData.firstName,
            lName: formData.lastName,
            email: formData.email,
          }
        }
      });

      toast({
        title: "Success",
        description: "Your profile has been updated",
        status: "success",
        duration: 3000,
      });

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update your profile",
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Complete Your Profile</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text mb={4}>
            Please provide your details before continuing with your booking.
          </Text>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>First Name</FormLabel>
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Last Name</FormLabel>
              <Input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
              />
            </FormControl>

            <Button
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={6}
              py={3}
              minH="3rem"
              fontSize="md"
              fontWeight="500"
              color="#2F855A"
              bg="transparent"
              border="2px solid"
              borderColor="current"
              borderRadius="md"
              transition="all 0.3s ease"
              onClick={handleSubmit}
              isLoading={loading}
              w="full"
              mt={4}
              _hover={{
                color: "#276749",
                bg: "#F0FFF4",
                transform: "translateY(-2px)",
                boxShadow: "4px 4px #C6F6D5",
                textDecoration: "none"
              }}
              _active={{
                transform: "translateY(0)",
                boxShadow: "none"
              }}
              _focus={{
                outline: "4px solid #9AE6B4",
                outlineOffset: "-1px"
              }}
            >
              Save and Continue
              <Box as="svg"
                h={5}
                w={5}
                viewBox="0 0 24 24"
                fill="currentColor"
                transition="transform 0.3s ease"
                _groupHover={{ transform: "translateX(4px)" }}
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </Box>
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}; 